import React, { FC, createContext, useState, useEffect, useMemo } from 'react'
import { IGif } from '@giphy/js-types'
import { VideoContextAPI } from '@giphy/react-giphy-brand'
import useLocalStorage from 'react-use/lib/useLocalStorage'
import { parseUrl } from 'shared/util/url'
import { useHistory } from 'react-router'

const MINUTES_TO_RESET = 5

type VideoOverlayContextProps = {
    onStartHover?: (gifId: string) => void
    onFinishHover?: (gifId: string) => void
    onFinishVolumeIconAnimation?: () => void
    setMuted: (muted: boolean, isMutedByBrowser?: boolean) => void
    muted?: boolean | undefined
    finishVolumeIconAnimation?: boolean
    hoveredClip?: string | null

    // Deal with loop through videos
    setMainVideoApi: (videoApi: VideoContextAPI) => void
    startNextVideo?: () => void
    setNextVideoToPlay?: (video: IGif | undefined) => void
    nextVideoToPlay?: IGif
    changeAutoplay: () => void
    autoplay: boolean
    loopCounter: number
    setLoopCounter: (counter: number) => void
    timeToNextVideo: number
    resetLoopToNextVideo: () => void
    setPreventLoopCounter: (prevent: boolean) => void

    // Handle up Next
    continueSameUpNext: boolean
    setContinueSameUpNext: (same: boolean) => void

    // Prevent hover to work after just start another video
    timeWhenVideoStarted: number | undefined
    setTimeWhenVideoStarted: (time: number) => void
}

type Props = {}

export const VideoContext = createContext({} as VideoOverlayContextProps)

const MINIMUM_TIME_UNTIL_GO_TO_NEXT_VIDEO = 15
const MINIMUM_OF_LOOPS = 3
const MAXIMUM_OF_LOOPS = 5

/*
 This context stores infos related to clips and handle behaviors such as the autoplay loop
 To read more info about it, you can check the video-manager.md
 */
const VideoContextManager: FC<Props> = ({ children }) => {
    // Prevent hover to work after just start another video
    const [timeWhenVideoStarted, setTimeWhenVideoStarted] = useState<number | undefined>()

    // Deal with loop through videos
    const history = useHistory()
    const [nextVideoToPlay, setNextVideoToPlay] = useState<IGif>()
    const [autoplay, setAutoplay] = useLocalStorage<boolean>('loop-autoplay', true)
    const [loopCounter, setLoopCounter] = useState<number>(0)
    const [continueSameUpNext, setContinueSameUpNext] = useState<boolean>(false)
    const [preventLoopCounter, setPreventLoopCounter] = useState<boolean>(false)
    const [mainVideoApi, setMainVideoApi] = useState<VideoContextAPI>()
    const [timeToNextVideo, setTimeToNextVideo] = useState<number>(-1)
    const startNextVideo = () => {
        setLoopCounter(0)

        if (nextVideoToPlay) {
            setContinueSameUpNext(true)
        }
    }
    useEffect(() => {
        if (continueSameUpNext && nextVideoToPlay) {
            const { pathname } = parseUrl(nextVideoToPlay.url)
            history.push(String(pathname))
        }
    }, [continueSameUpNext])
    const handleLoopCounter = (counter: number) => {
        if (autoplay && !preventLoopCounter) {
            setLoopCounter(counter)
        }
    }
    const handlePreventLoopCounter = preventLoopCounter => {
        if (preventLoopCounter) {
            setLoopCounter(Math.round(loopCounter / 2))
        }
        setPreventLoopCounter(preventLoopCounter)
    }
    useEffect(() => {
        if (!autoplay) {
            setTimeToNextVideo(-1)
            setLoopCounter(0)
        }
    }, [autoplay])
    useEffect(() => {
        setTimeToNextVideo(-1)
        setLoopCounter(0)
    }, [mainVideoApi])
    useEffect(() => {
        if (mainVideoApi?.getDuration() && loopCounter > 0) {
            let loopToGoNext = Math.round(MINIMUM_TIME_UNTIL_GO_TO_NEXT_VIDEO / mainVideoApi?.getDuration())

            if (loopToGoNext < MINIMUM_OF_LOOPS) {
                loopToGoNext = MINIMUM_OF_LOOPS
            }

            if (loopToGoNext > MAXIMUM_OF_LOOPS) {
                loopToGoNext = MAXIMUM_OF_LOOPS
            }

            if (loopCounter < loopToGoNext - 2) {
                setTimeToNextVideo(-1)
            }
            if (loopCounter === loopToGoNext - 1) {
                const duration = mainVideoApi?.getDuration() || -1
                duration && setTimeToNextVideo(Math.round(duration))
            }
        }
    }, [loopCounter])
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>

        if (timeToNextVideo === 0) {
            setTimeToNextVideo(-1)
            startNextVideo?.()
        } else {
            timer = setInterval(() => {
                if (timeToNextVideo > -1) {
                    setTimeToNextVideo(timeToNextVideo - 1)
                }
            }, 1000)
        }

        return () => clearInterval(timer)
    }, [timeToNextVideo])

    // Deal with mute state of videos
    const [lastTimeMuted, setLastTimeChangeMuted] = useLocalStorage<number | null>('has-clips-muted', null)
    const startsMuted = useMemo(() => {
        // Check if user set muted for more than MINUTES_TO_RESET minutes, IF yes, reset the mute state
        if (lastTimeMuted === null) return undefined

        const minutesOfDifference = (new Date().getTime() - lastTimeMuted) / 60000

        if (minutesOfDifference > MINUTES_TO_RESET) {
            return undefined
        } else {
            return true
        }
    }, [])
    const [hoveredClip, setHoveredClip] = useState<null | string>(null)
    const [muted, setIsMuted] = useState<boolean | undefined>(startsMuted)
    const [finishVolumeIconAnimation, setFinishVolumeIconAnimation] = useState(false)
    const handleMuted = (muted: boolean, isMutedByBrowser?: boolean) => {
        setIsMuted(muted)

        if (muted && !isMutedByBrowser) {
            setLastTimeChangeMuted(new Date().getTime())
        } else {
            setLastTimeChangeMuted(null)
        }
    }

    return (
        <VideoContext.Provider
            value={{
                onStartHover: (gifId: string) => setHoveredClip(gifId),
                onFinishHover: () => setHoveredClip(null),
                onFinishVolumeIconAnimation: () => setFinishVolumeIconAnimation(true),
                finishVolumeIconAnimation,
                hoveredClip,
                muted,
                setMuted: (muted, isMutedByBrowser) => handleMuted(muted, isMutedByBrowser || false),

                // Video Loop to next
                setMainVideoApi: mainVideoApi => setMainVideoApi(mainVideoApi),
                setLoopCounter: counter => handleLoopCounter(counter),
                startNextVideo: () => startNextVideo(),
                setNextVideoToPlay: (video: IGif) => setNextVideoToPlay(video),
                resetLoopToNextVideo: () => handleLoopCounter(Math.round(loopCounter / 2)),
                nextVideoToPlay,
                changeAutoplay: () => setAutoplay(!autoplay),
                setPreventLoopCounter: preventLoopCounter => handlePreventLoopCounter(preventLoopCounter),
                autoplay,
                loopCounter,
                timeToNextVideo,

                // Handle Up Next
                continueSameUpNext,
                setContinueSameUpNext: same => setContinueSameUpNext(same),

                // Prevent hover to work after just start another video
                timeWhenVideoStarted,
                setTimeWhenVideoStarted: time => setTimeWhenVideoStarted(time),
            }}
        >
            {children}
        </VideoContext.Provider>
    )
}

export default VideoContextManager
